import type { StepLogRow } from '@ctcells/api'
import { executionEventLabel, hasMessage, t } from '@ctcells/i18n'

import { deviceKeyOfModuleType, type MonitoringDeviceKey } from './monitoringModel.ts'

/**
 * 시퀀스 이름 사전 — 장비마다 갈린다(같은 원값이 장비마다 다른 일이라 한 사전으로 합치지 않는다 · 예 `open_door` 는 염색기에서 「후문 열기」).
 * 네 장비 다 module-core `profiles/*.yaml` `supported_sequences` 전수로 채웠다(2026-10-11).
 */
export type SequenceDictionary =
  'enum.robotSequence' | 'enum.stainSequence' | 'enum.centSequence' | 'enum.asmSequence'

/** 장비 → 시퀀스 이름 사전 — 🔴 정의처 하나(장비 그림 아래 한 줄 · 작업 줄 3행이 같이 쓴다) */
export const SEQUENCE_DICTIONARY: Readonly<Record<MonitoringDeviceKey, SequenceDictionary>> = {
  robot: 'enum.robotSequence',
  stain: 'enum.stainSequence',
  cent: 'enum.centSequence',
  asm: 'enum.asmSequence',
}

/** 모듈 종류 원값(`centrifuge` · `collaborative_6axis` …) → 사전. 모르는 종류 · 없음이면 `undefined`(짐작하지 않는다) */
export function sequenceDictionaryOfModuleType(
  moduleType: string | null | undefined,
): SequenceDictionary | undefined {
  const key = deviceKeyOfModuleType(moduleType)
  return key === undefined ? undefined : SEQUENCE_DICTIONARY[key]
}

/**
 * 모듈의 말 한 줄의 머리(시퀀스 원값)를 사전 이름으로 — 🔴 `action`(같은 줄의 구조화 값)이 머리와 «정확히» 같을 때만 · 뒤는 그대로.
 * 사전 · `action` 이 없거나 · 사전에 없는 이름이거나 · 머리가 다른 이름(`move_to` ↔ `move_to_module`)이면 말 그대로.
 */
export function sequenceText(
  message: string,
  action: string | undefined,
  dictionary: SequenceDictionary | undefined,
): string {
  if (dictionary === undefined || action === undefined || !message.startsWith(action))
    return message
  const rest = message.slice(action.length)
  // 머리가 «그 이름 하나»일 때만(`move_to` 와 `move_to_module` 을 섞지 않는다) — 끝 · 띄어쓰기 · 쌍점
  if (rest !== '' && !rest.startsWith(' ') && !rest.startsWith(':')) return message
  /*
   * 🔴 말에 붙일 땐 **명사형**이 먼저다(`<사전>Step.<원값>` · 예 `enum.stainSequenceStep.dispense_reagent` 「시약 넣기」) — 사전 이름이 진행형
   *    (「시약 넣는 중」)이면 「… 중 완료」가 된다(2026-10-11 캡처). 명사형 키가 없으면 사전 이름(이미 명사형) 그대로.
   */
  const step = `${dictionary}Step.${action}`
  if (hasMessage(step)) return `${t(step)}${stepTailText(rest)}`
  const key = `${dictionary}.${action}`
  return hasMessage(key) ? `${t(key)}${stepTailText(rest)}` : message
}

/**
 * 모듈의 말 꼬리 — 정해진 두 꼴(` 시작` · ` 완료`)만 사전으로 옮긴다. 영어 화면에 「Move Z 완료」가 서지 않게. 한국어는 같은 글자다.
 * 그 밖(`: 까닭`)은 모듈의 문장이라 그대로 둔다 — 문장을 해석해 뜻을 짓지 않는다.
 */
function stepTailText(rest: string): string {
  if (rest === ' 시작') return ` ${t('monitoring.step.started')}`
  if (rest === ' 완료') return ` ${t('monitoring.step.done')}`
  return rest
}

/** 그 모듈의 가장 최근 스텝 줄 — 받은 차례(오래된 → 최근)의 뒤에서부터. 없으면 `undefined` */
export function latestStepOf(
  rows: readonly StepLogRow[],
  moduleId: string | undefined,
): StepLogRow | undefined {
  if (moduleId === undefined) return undefined
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    const row = rows[i]
    if (row?.moduleId === moduleId) return row
  }
  return undefined
}

/**
 * 장치 그림 아래 한 줄의 글자 — 이벤트 로그 「내용」 열의 **머리만**(모듈의 말 · 없으면 생애 라벨). 🔴 스텝 수(「1044/4211」)는 붙이지 않는다
 * (eric 2026-10-08 「내용만 보여달라고. 스텝 보여달라는 얘기했어? 안했어?」). 이벤트 칸(`stepContentText`)과 머리는 같은 값이다.
 *
 * 🔴 **시퀀스 원값을 영문 그대로 흘리지 않는다**(eric 2026-10-11 캡처 — 6축 그림 아래 「move_aux_axis 완료」). 모듈의 말은
 *    `{sequence} 시작` · `{sequence} 완료` · `{sequence}: 까닭` 꼴이다(module-core `drivers/sim.py` · `real.py`). 그 줄이 지닌 **구조화 값**
 *    `action`(같은 시퀀스 원값 · `stepAction`)이 말의 머리와 «정확히» 같을 때만 그 머리를 장비 사전 이름으로 바꾼다 — 뒤는 모듈의 말 그대로.
 *    문장을 해석해 뜻을 짓지 않는다. 사전에 없는 시퀀스 · 사전을 안 넘긴 자리면 종전 그대로(`enumLabel` 규약 — 모르는 이름은 원값).
 * 🔴 eric 2026-10-11 — 원심 · 어셈블러 · 염색기 그림 아래도 「move_z 완료」 · 「move_to 완료」 가 영문으로 섰다 → 네 장비 다 사전을 건다.
 */
export function latestStepText(row: StepLogRow, dictionary?: SequenceDictionary): string {
  return stepHeadText(row, dictionary)
}

/**
 * 한 줄의 머리 — 🔴 **모듈의 말을 그대로 쓰는 것은 «스텝 완료»뿐이다**(eric 2026-10-12 「취소됐다 이런 게 화면에 나오면 안 되지」).
 *    시작 · 완료 · 취소 · 중단 · 오류 · 일시정지 · 재개 같은 생애 사건의 `message` 는 모듈이 실행을 닫으며 싣는 내부 사유 원문이다
 *    (module-core `ex.terminal_reason` 「취소됐다」 · 「실행 시작 — 스텝 N개」 · 드라이버 예외 문장). 그 자리에는 우리 사전의 사건 라벨을 쓰고,
 *    시퀀스 이름을 사전에서 찾으면 앞에 붙인다(「시약 넣기 취소」). 사전에 없는 시퀀스 원값은 붙이지 않는다(영문을 흘리지 않는다).
 *    오류의 자세한 까닭은 알람이 사람 말로 낸다 — 여기서 모듈 문장을 옮기지 않는다.
 */
/** 모듈의 말(`message`)을 화면에 옮겨도 되는 사건인가 — 스텝 완료 · 진행 프레임뿐(생애 사건의 말은 내부 사유 원문이다) */
export function speaksSequence(eventType: string | undefined): boolean {
  return eventType === undefined || eventType === 'step_complete' || eventType === 'progress'
}

export function stepHeadText(row: StepLogRow, dictionary?: SequenceDictionary): string {
  if (row.eventType === 'step_complete' && row.message !== undefined)
    return sequenceText(row.message, row.action, dictionary)
  const label = executionEventLabel(row.eventType)
  const name = sequenceName(row.action, dictionary)
  return name === undefined ? label : `${name} ${label}`
}

/** 시퀀스 원값의 사전 이름(명사형 먼저) — 사전 · 원값이 없거나 사전에 없으면 `undefined` */
function sequenceName(
  action: string | undefined,
  dictionary: SequenceDictionary | undefined,
): string | undefined {
  if (dictionary === undefined || action === undefined) return undefined
  const step = `${dictionary}Step.${action}`
  if (hasMessage(step)) return t(step)
  const key = `${dictionary}.${action}`
  return hasMessage(key) ? t(key) : undefined
}
